import React, { useState } from 'react';

import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Input } from 'fpay-ui';

import { GenericForm } from '@components';
import Button from '@components/Forms/Button';
import FormPasswordConfirmation from '@components/Forms/FormPasswordConfirmation';
import { parsePhoneObject } from '@helpers';
import { formFieldsHaveValue } from '@helpers/validator';
import { useAuth, useUser, useEmailAge } from '@hooks';
import { regionPhoneCode } from '@constants/regions';

import schema from './schema';

const Card = styled.div`
  padding: 32px;
  background-color: #fff;
  width: 500px;
  border-radius: 6px;
  display: initial;
  position: initial;
  flex-direction: initial;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    display: flex;
    position: relative;
    flex-direction: column;
    padding: 32px 24px 107px;
  }
  div {
    margin-left: 0;
    margin-right: 0;
    div {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

const Title = styled.h3(
  ({ theme }) => css`
    margin-top: 0;
    margin-bottom: 29px;
    ${theme.textTypes['H6-w400']};
    ${({ theme }) => theme.newBreakpoint('sm')} {
      ${theme.textTypes['Subtitle-1-w400']};
    }
    color: ${theme.colors.gray800};
  `
);

const StyledInput = styled(Input)`
  margin-bottom: 21px;
`;

const StyledPhone = styled(Input)`
  margin-bottom: 5px;
`;

const ButtonCancel = styled(Button)`
  max-width: 102px;
  margin-right: 16px !important;
  height: 48px !important;
  display: initial;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    display: none !important;
  }
`;
const ButtonSubmit = styled(Button)`
  max-width: 102px;
  height: 48px !important;
  position: initial;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    position: absolute !important;
    max-width: calc(100% - 48px);
    bottom: 32px;
    left: 24px;
    right: 24px;
  }
`;

const FormRepresentant = ({}) => {
  const { t } = useTranslation();
  const { merchant, updateRepresentant, isLegalEntity } = useAuth();
  const { users } = useUser();
  const userLegalAgent = users.find((el) => el.is_legal_agent);
  const history = useHistory();
  const [anyChange, setChange] = useState(false);
  const [isNewEmail, setIsNewEmail] = useState(false);
  const [isNewPhone, setIsNewPhone] = useState(false);
  const [showFormPasswordConfirmation, setShowFormPasswordConfirmation] = useState(false);
  const [representant, setRepresentant] = useState(
    JSON.parse(JSON.stringify(merchant?.legal_agent))
  );
  const { isEmailAgeInvalid, setIsEmailAgeInvalid, isErrorEmailAge } = useEmailAge();

  const handleChange = (e) => {
    const { target } = e;
    setChange(true);
    if (target.id === 'phone_number') {
      setRepresentant({ ...representant, phones: [parsePhoneObject(target.value)] });
      setIsNewPhone(true);
      return;
    }
    if (target.id === 'email') setIsEmailAgeInvalid(false);

    setRepresentant({ ...representant, [target.id]: target.value });
    setIsNewEmail(true);
  };

  const updateRepresentatError = (error) => {
    if (isErrorEmailAge(error)) {
      setShowFormPasswordConfirmation(false);
    }
  };

  const handleSubmit = (password = null) => {
    updateRepresentant(
      {
        legalAgentId: userLegalAgent._id,
        representant,
        isNewPhone,
        isNewEmail,
        password,
      },
      updateRepresentatError
    );
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleConfirmation = () => {
    if (isNewEmail) {
      setShowFormPasswordConfirmation(true);
      return;
    }

    handleSubmit();
  };

  const handleCancelPasswordConfirmation = () => {
    setShowFormPasswordConfirmation(false);
  };

  const isSubmitDisabled =
    !anyChange ||
    formFieldsHaveValue([
      representant?.email,
      representant?.phones?.length > 0
        ? representant?.phones[0].region_id + representant.phones[0].number
        : '',
    ]);

  return showFormPasswordConfirmation ? (
    <FormPasswordConfirmation
      email={representant.email}
      handleCancel={handleCancelPasswordConfirmation}
      handleSubmit={handleSubmit}
    />
  ) : (
    <Card>
      <Title>{t('commerceData.editRepresentant.subtitle')}</Title>
      <GenericForm
        schema={schema}
        justify="flex-end"
        item
        xs={12}
        container
        spacing={1}
        onSubmit={handleConfirmation}
      >
        <StyledInput
          id="first_name"
          name="first_name"
          testId="first_name"
          gridProps={{ xs: 12, item: true }}
          value={representant?.first_name}
          label={t('commerceData.editRepresentant.firstName')}
          disabled={true}
        />
        <StyledInput
          id="patern_last"
          name="patern_last"
          testId="patern_last"
          gridProps={{ xs: 12, item: true }}
          value={representant?.patern_last}
          label={t('commerceData.editRepresentant.paternLast')}
          disabled={true}
        />
        <StyledInput
          id="matern_last"
          name="matern_last"
          testId="matern_last"
          gridProps={{ xs: 12, item: true }}
          value={representant?.matern_last}
          label={t('commerceData.editRepresentant.maternLast')}
          disabled={true}
        />
        {isLegalEntity() ? (
          <StyledInput
            id="doc_number"
            name="doc_number"
            testId="doc_number"
            gridProps={{ xs: 12, item: true }}
            value={representant?.document?.doc_number}
            label={t('commerceData.editRepresentant.docNumber')}
            disabled={true}
          />
        ) : (
          <></>
        )}
        <StyledInput
          id="email"
          name="email"
          testId="email"
          gridProps={{ item: true, xs: 12 }}
          onChange={handleChange}
          value={representant?.email}
          label={t('commerceData.editRepresentant.email')}
          error={isEmailAgeInvalid}
        />
        <StyledPhone
          gridProps={{ item: true, xs: 12 }}
          id="phone_number"
          name="phone_number"
          testId="phone_number"
          label={t('commerceData.editRepresentant.phone')}
          value={
            representant?.phones?.length > 0
              ? representant.phones[0].region_id + representant.phones[0].number
              : null
          }
          onChange={handleChange}
          countryCode={regionPhoneCode}
        />
        <>
          <ButtonCancel
            onClick={handleCancel}
            variant="outlined"
            data-testid="formrepresentant-cancel-button"
          >
            {t('commerceData.editRepresentant.cancel')}
          </ButtonCancel>
          <ButtonSubmit
            type="submit"
            data-testid="formrepresentant-save-button"
            disabled={isSubmitDisabled || isEmailAgeInvalid}
          >
            {t('commerceData.editRepresentant.save')}
          </ButtonSubmit>
        </>
      </GenericForm>
    </Card>
  );
};

FormRepresentant.propsTypes = {};

FormRepresentant.defaultProps = {};

export default FormRepresentant;
